import type { Feature, Polygon, MultiPolygon, Geometry } from "geojson";
import wkx from "wkx";
import type * as L from "leaflet";
import booleanIntersects from "@turf/boolean-intersects";
import { polygon as turfPolygon, feature as turfFeature } from "@turf/helpers";
import type { Segment } from "src/types";

export function featureToWKT(data: Feature): string {
  const wktGeometry = wkx.Geometry.parseGeoJSON(data.geometry);
  return wktGeometry.toWkt();
}

export function latlngToWkt(latlng: L.LatLngLiteral): string {
  const { lat, lng } = latlng;
  return new wkx.Point(lng, lat).toWkt();
}

export function wktPointToLatlng(wkt: string): L.LatLngLiteral {
  const point = wkx.Geometry.parse(wkt) as wkx.Point;
  return { lat: point.y, lng: point.x };
}

export function wktPolygonToFeature(wkt: string): Feature {
  const geometry = wkx.Geometry.parse(wkt).toGeoJSON();
  // const feature = turfFeature(geometry) as Feature<Polygon | MultiPolygon>;
  const feature = turfFeature(geometry) as Feature<Polygon | MultiPolygon>;
  return feature;
}

export function mergePolygonFeatures(features: Feature<Polygon | MultiPolygon>[]): Feature<MultiPolygon> {
  const coordinates = features.reduce<number[][][][]>((acc, feature) => {
    if (feature.geometry.type === "Polygon") {
      acc.push(feature.geometry.coordinates);
    } else if (feature.geometry.type === "MultiPolygon") {
      acc.push(...feature.geometry.coordinates);
    }
    return acc;
  }, []);

  return {
    type: "Feature",
    properties: {},
    geometry: {
      type: "MultiPolygon",
      coordinates,
    },
  };
}

export function isPolygonOrMultiPolygon(feature: Feature): feature is Feature<Polygon | MultiPolygon> {
  return feature.geometry.type === "Polygon" || feature.geometry.type === "MultiPolygon";
}

export function segmentToFeature(segment: Segment): Feature {
  const geometry = wkx.Geometry.parse(segment.lineString_frontendSrid);
  const geoJSON = geometry.toGeoJSON() as Geometry;
  return turfFeature(geoJSON, { id: segment.segmentId });
}

export function findIntersectingSegments(polygon: Feature<Polygon>, segments: Segment[]): number[] {
  const intersectingIds: number[] = [];

  for (const segment of segments) {
    try {
      const segmentFeature = segmentToFeature(segment);
      if (booleanIntersects(polygon, segmentFeature)) {
        intersectingIds.push(segment.segmentId);
      }
    } catch (error) {
      console.warn(`Intersection check failed for segment ${segment.segmentId}`, error);
    }
  }

  return intersectingIds;
}
